import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'
import type { DigitalProduct } from '@/types/digitalProduct'

import { AuthHero } from './AuthHero'
import { AuthPageFooter } from './AuthPageFooter'
import { AuthValueProps } from './AuthValueProps'

type AuthPageLayoutProps = {
  digitalProduct?: DigitalProduct
  children: ReactNode
  pageAction?: 'sign-in' | 'sign-up' | 'sub-route'
  showValueProps?: boolean
  showHero?: boolean
  /** Oculta rodapé de consentimento/cadastro (ex.: acesso pendente). */
  showFooter?: boolean
  /** Mantém o conteúdo principal no topo, sem centralização vertical. */
  anchorContentTop?: boolean
}

/**
 * Full-page auth shell. Centraliza o bloco de conteúdo na viewport quando há
 * espaço sobrando; com overflow, o container cresce e o scroll assume.
 */
export function AuthPageLayout({
  digitalProduct,
  children,
  pageAction = 'sign-in',
  showValueProps = true,
  showHero = true,
  showFooter = true,
  anchorContentTop = false
}: AuthPageLayoutProps) {
  const isSignIn = pageAction === 'sign-in'
  const showMarketing = showHero || (showValueProps && isSignIn)

  const safeAreaPadding = cn(
    'pt-[max(1.5rem,env(safe-area-inset-top,0px))]',
    'pb-[max(1.25rem,calc(env(safe-area-inset-bottom,0px)+1rem))]',
    'short:pt-[max(1rem,calc(env(safe-area-inset-top,0px)+0.75rem))]',
    'short:pb-[max(1rem,calc(env(safe-area-inset-bottom,0px)+0.75rem))]',
    'sm:pt-8 sm:pb-[max(1.5rem,calc(env(safe-area-inset-bottom,0px)+1.25rem))]'
  )

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain bg-white">
      <div
        className={cn(
          'mx-auto flex min-h-[100dvh] w-full max-w-[400px] flex-col px-5 sm:px-6',
          safeAreaPadding,
          !anchorContentTop && 'justify-center'
        )}
      >
        <div
          className={cn(
            'flex w-full flex-col',
            !anchorContentTop && 'gap-4 short:gap-3'
          )}
        >
          {showMarketing ? (
            <div className="flex shrink-0 flex-col">
              {showHero ? (
                <AuthHero
                  digitalProduct={digitalProduct}
                  showValueProps={showValueProps && isSignIn}
                />
              ) : showValueProps && isSignIn ? (
                <AuthValueProps />
              ) : null}
            </div>
          ) : null}

          <div className="flex w-full shrink-0 flex-col">
            <main className="w-full shrink-0">{children}</main>
            {showFooter ? <AuthPageFooter pageAction={pageAction} /> : null}
          </div>
        </div>

        {anchorContentTop ? (
          <div
            aria-hidden
            className="mt-4 h-4 shrink-0 short:mt-2 short:h-2"
          />
        ) : null}
      </div>
    </div>
  )
}
